/**
 * Licensed Materials - Property of HCL Technologies Limited.
 * (C) Copyright HCL Technologies Limited  2023.
 */

import { Linkable } from '@/components/blocks/Linkable';
import { Img } from '@/components/blocks/MaterialImage';
import { ProgressIndicator } from '@/components/blocks/ProgressIndicator';
import { useCategory } from '@/data/Content/Category';
import { useChildCategoryGrid } from '@/data/Content/ChildCategoryGrid';
import { useSeriesBySeries } from '@/data/Content/Series';
import { useLocalization } from '@/data/Localization';
import { ID } from '@/data/types/Basic';
import { CategoryType } from '@/data/types/Category';
import { Box, Button, Skeleton, Stack, Typography } from '@mui/material';
import {
	seriesColorImageWrapper45SX,
	seriesColorSX,
	seriesColorTextSX,
	seriesColorWrapperSingleRowSX,
} from 'presentation/components/fiwe/widgets/CategorySeriesProductListWidget/styles/seriesColor';
import {
	seriesDimensionListSX,
	seriesDimensionTextSX,
} from 'presentation/components/fiwe/widgets/CategorySeriesProductListWidget/styles/seriesDimension';
import { FC } from 'react';

export const CategoryMarketingConceptsWidget: FC<{ id: ID }> = ({ id }) => {
	const categoryText = useLocalization('Category');
	const { categories, loading: childrenLoading } = useChildCategoryGrid(id);

	return (
		<Box data-id="CategoryMarketingConceptsWidget">
			{childrenLoading ? (
				<ProgressIndicator />
			) : (
				<Stack>
					{categories?.map((child) => (
						<Stack rowGap={2} key={child.identifier} sx={{ marginBottom: 8 }}>
							<Box>
								<Linkable href={child.seo.href}>
									<Typography variant="h2_underline">{child.name}</Typography>
								</Linkable>
								<Typography variant="body1">
									<Typography
										component={'span'}
										sx={{ fontWeight: 700, textTransform: 'uppercase' }}
									>
										{child.shortDescription?.slice(0, child.shortDescription.indexOf(' '))}{' '}
									</Typography>
									{child.shortDescription?.slice(child.shortDescription.indexOf(' '))}
								</Typography>
							</Box>
							<ColorsGrid category={child} />
							<Button
								variant="contained"
								color="primary"
								href={child.seo.href}
								sx={{ alignSelf: 'flex-start' }}
							>
								{categoryText.ShowAll.t()}
							</Button>
						</Stack>
					))}
				</Stack>
			)}
		</Box>
	);
};

// Grid variant
const ColorsGrid: FC<{
	category: CategoryType;
}> = ({ category }) => {
	const { categories, loading: childrenLoading } = useChildCategoryGrid(category.uniqueID);
	return (
		<Box sx={seriesColorWrapperSingleRowSX}>
			{childrenLoading
				? [...Array(3)].map((x, i) => (
						<Box sx={seriesColorSX({ margin: false })} key={i}>
							<Skeleton variant="rectangular" sx={{ height: 'auto', paddingTop: '100%' }} />
							<Skeleton variant="text" />
						</Box>
				  ))
				: categories?.map((child) => (
						<Box key={child.identifier} sx={seriesColorSX({ margin: false })}>
							<Linkable href={child.seo.href} sx={seriesColorImageWrapper45SX}>
								{/* <Img src={child.thumbnail} alt={child.name} /> */}
								<CategoryFullImage categoryId={child.uniqueID} />
								<Typography sx={seriesColorTextSX}>{child.name}</Typography>
							</Linkable>
							<DimensionList series={child?.name} />
						</Box>
				  ))}
		</Box>
	);
};

const CategoryFullImage: FC<{
	categoryId: string;
}> = ({ categoryId }) => {
	const { category, loading } = useCategory(categoryId);
	return loading ? null : category ? (
		<Img src={category.fullImage} alt={category.name} loading="lazy" />
	) : null;
};

// List of all dimensions for a series / category
const DimensionList: FC<{
	series: string;
}> = ({ series }) => {
	const { isLoading, dimensions } = useSeriesBySeries(series ?? null);

	return (
		<Box>
			{isLoading ? (
				<Skeleton variant="text" />
			) : (
				<Stack sx={seriesDimensionListSX} direction={'row'} spacing={0}>
					{dimensions?.map((dimension) => (
						<Typography key={dimension.catentryId} sx={seriesDimensionTextSX}>
							{dimension.dimension}
						</Typography>
					))}
				</Stack>
			)}
		</Box>
	);
};
